import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { refreshContent } from "@/lib/content";
import { PageHeader, GlassCard } from "../_shared";

function Section({ title, description, children }) {
  return (
    <GlassCard className="p-6">
      <div className="mb-4">
        <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
        {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </GlassCard>
  );
}

function TextField({ label, value, onChange, placeholder, type = "text", help, testid, full = false }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-sm font-semibold">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" data-testid={testid} />
      {help && <p className="text-xs text-slate-500 mt-1">{help}</p>}
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder, rows = 3, testid, full = false }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-sm font-semibold">{label}</Label>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} className="mt-1" data-testid={testid} />
    </div>
  );
}

function SwitchField({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between bg-white/50 rounded-xl border border-slate-200 px-4 py-3 sm:col-span-2">
      <div>
        <Label className="font-semibold">{label}</Label>
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

export default function SettingsManager() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/cms/settings").then(r => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k) => (v) => setS((prev) => ({ ...prev, [k]: v }));
  const setHours = (i, k) => (v) => setS((prev) => {
    const h = [...(prev.hours || [])];
    h[i] = { ...h[i], [k]: v };
    return { ...prev, hours: h };
  });
  const addHours = () => setS((prev) => ({ ...prev, hours: [...(prev.hours || []), { day: "", time: "" }] }));
  const removeHours = (i) => setS((prev) => ({ ...prev, hours: (prev.hours || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/cms/settings", s);
      toast.success("Settings saved");
      refreshContent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="text-slate-500">Loading…</div>;

  return (
    <>
      <PageHeader
        title="Site Settings"
        subtitle="Business info, contact details, social links and SEO metadata. Applies site-wide."
        actions={
          <Button onClick={save} disabled={busy} className="bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full" data-testid="save-settings-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
          </Button>
        }
      />

      <div className="grid gap-6">
        <Section title="Business Info" description="Displayed across header, footer, and structured data.">
          <TextField label="Business name" value={s.name} onChange={set("name")} testid="settings-name" />
          <TextField label="Owner name" value={s.owner} onChange={set("owner")} testid="settings-owner" />
          <TextField label="Tagline" value={s.tagline} onChange={set("tagline")} full testid="settings-tagline" />
          <TextField label="Phone (dial format)" value={s.phone} onChange={set("phone")} placeholder="7603099563" testid="settings-phone" />
          <TextField label="Phone (display format)" value={s.phone_display} onChange={set("phone_display")} placeholder="+91 76030 99563" testid="settings-phone-display" />
          <TextField label="Email" value={s.email} onChange={set("email")} type="email" testid="settings-email" />
          <TextField label="WhatsApp number (with country code, no +)" value={s.whatsapp} onChange={set("whatsapp")} placeholder="917603099563" testid="settings-whatsapp" />
          <TextAreaField label="Address" value={s.address} onChange={set("address")} full rows={2} testid="settings-address" />
          <TextField label="Logo URL" value={s.logo_url} onChange={set("logo_url")} full testid="settings-logo" />
        </Section>

        <Section title="Opening Hours" description="Shown in the footer and Contact page.">
          <div className="sm:col-span-2 space-y-2">
            {(s.hours || []).map((h, i) => (
              <div key={i} className="flex gap-2">
                <Input value={h.day || ""} onChange={(e) => setHours(i, "day")(e.target.value)} placeholder="e.g. Mon – Sat" className="flex-1" />
                <Input value={h.time || ""} onChange={(e) => setHours(i, "time")(e.target.value)} placeholder="e.g. 10:00 AM – 8:00 PM" className="flex-1" />
                <Button variant="outline" onClick={() => removeHours(i)}>Remove</Button>
              </div>
            ))}
            <Button variant="outline" onClick={addHours}>Add row</Button>
          </div>
        </Section>

        <Section title="Google Maps & Reviews" description="Point the map + review buttons to your verified Google Business listing.">
          <TextField label="Map embed URL" value={s.map_embed} onChange={set("map_embed")} full testid="settings-map-embed" />
          <TextField label="Google Maps share link" value={s.maps_link} onChange={set("maps_link")} full testid="settings-maps-link" />
          <TextField label="Write a review link" value={s.write_review_link} onChange={set("write_review_link")} full testid="settings-write-review" />
          <TextField label="Directions link" value={s.directions_link} onChange={set("directions_link")} full testid="settings-directions" />
          <TextField label="Rating (e.g. 4.8)" value={s.rating_value} onChange={(v) => set("rating_value")(Number(v) || null)} testid="settings-rating" />
          <TextField label="Review count (e.g. 61)" value={s.rating_count} onChange={(v) => set("rating_count")(parseInt(v) || null)} testid="settings-rating-count" />
        </Section>

        <Section title="Social Links" description="Optional. Empty fields are hidden on the site.">
          <TextField label="Facebook URL" value={s.social_facebook} onChange={set("social_facebook")} testid="settings-facebook" />
          <TextField label="Instagram URL" value={s.social_instagram} onChange={set("social_instagram")} testid="settings-instagram" />
          <TextField label="YouTube URL" value={s.social_youtube} onChange={set("social_youtube")} testid="settings-youtube" />
          <TextField label="X / Twitter URL" value={s.social_x} onChange={set("social_x")} testid="settings-x" />
        </Section>

        <Section title="SEO & Analytics" description="Applied globally to <head> meta tags and structured data.">
          <TextField label="SEO Title" value={s.seo_title} onChange={set("seo_title")} full testid="settings-seo-title" />
          <TextAreaField label="Meta Description" value={s.seo_description} onChange={set("seo_description")} full rows={2} testid="settings-seo-description" />
          <TextField label="Keywords (comma separated)" value={s.seo_keywords} onChange={set("seo_keywords")} full testid="settings-keywords" />
          <TextField label="Google Analytics Measurement ID" value={s.ga_measurement_id} onChange={set("ga_measurement_id")} placeholder="G-XXXXXXXXXX" testid="settings-ga" />
          <TextField label="Google Tag Manager ID" value={s.gtm_id} onChange={set("gtm_id")} placeholder="GTM-XXXXXX" testid="settings-gtm" />
          <TextField label="Meta / Facebook Pixel ID" value={s.meta_pixel_id} onChange={set("meta_pixel_id")} placeholder="123456789" testid="settings-pixel" />
        </Section>

        <Section title="Feature Flags">
          <SwitchField label="Show announcement bar at top of site" checked={s.announcement_bar_enabled} onChange={set("announcement_bar_enabled")} testid="settings-announcement-toggle" />
        </Section>
      </div>
    </>
  );
}
